import { cn } from "@/shared/lib/utils"
import { composeEventHandlers } from "@/shared/lib/utils/compose-event-handlers"

import styles from "./action-button.module.css"

/**
 * The look of a confirmation button for an irreversible action. No display utility here: the loading
 * spinner relies on the button's own inline grid.
 */
export const DESTRUCTIVE_ACTION_CLASS =
  "min-h-12 rounded-xl bg-red-500 px-4 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-300 hover-device:hover:bg-red-400"

interface ActionButtonProps extends React.ComponentProps<"button"> {
  /** A request is in flight: the button says so, refuses the click, and keeps focus. */
  isLoading?: boolean
}

/**
 * A button that stays focusable while its action is in flight.
 *
 * `isLoading` sets `aria-disabled`, not the native `disabled`: the user has just pressed this
 * button, and disabling it would drop it out of the focus order. `aria-disabled` changes no
 * behaviour on its own, so the press is refused here — including the caller's `onClick`, so no
 * caller has to repeat `if (isLoading) return` to avoid a second request on a second tap.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-disabled
 *
 * Use native `disabled` for a button that cannot act yet and that the user has not touched.
 * `type` defaults to `"button"` so nothing submits a form unless its call site says so.
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  isLoading = false,
  type = "button",
  children,
  className,
  onClick,
  "aria-disabled": ariaDisabled,
  ...props
}) => {
  // React renders `aria-disabled={false}` as the string "false", so presence alone would freeze an available button.
  const isAriaDisabled = isLoading || ariaDisabled === true || ariaDisabled === "true"

  // Runs first: cancelling the event stops `composeEventHandlers` before the caller's handler and the form submission.
  const refusePress: React.MouseEventHandler<HTMLButtonElement> = event => {
    if (isAriaDisabled) event.preventDefault()
  }

  return (
    <button
      {...props}
      className={cn(styles.action, className)}
      type={type}
      aria-disabled={isAriaDisabled || undefined}
      data-loading={isLoading || undefined}
      onClick={composeEventHandlers(refusePress, onClick)}>
      {/* Label and spinner share one grid cell and are both always mounted, so the button neither
          resizes mid-press nor restarts the rotation; a bare string child could not be targeted. */}
      <span className={styles.label}>{children}</span>
      <svg
        className={styles.spinner}
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
        focusable="false">
        <path d="M8 0c-4.355 0-7.898 3.481-7.998 7.812 0.092-3.779 2.966-6.812 6.498-6.812 3.59 0 6.5 3.134 6.5 7 0 0.828 0.672 1.5 1.5 1.5s1.5-0.672 1.5-1.5c0-4.418-3.582-8-8-8zM8 16c4.355 0 7.898-3.481 7.998-7.812-0.092 3.779-2.966 6.812-6.498 6.812-3.59 0-6.5-3.134-6.5-7 0-0.828-0.672-1.5-1.5-1.5s-1.5 0.672-1.5 1.5c0 4.418 3.582 8 8 8z" />
      </svg>
    </button>
  )
}

ActionButton.displayName = "ActionButton"
